import { t } from '../i18n.js';

class UserList extends HTMLElement {

    // ── A. Shadow DOM setup ──────────────────────────────────────────────
    constructor() {
        super();
        this._shadow = this.attachShadow({ mode: "open" });
        this._users = [];
        this._query = "";
        this._filter = "all";
    }

    // ── B. Lifecycle: element added to DOM ───────────────────────────────
    connectedCallback() {
        this._render();
        this._bindEvents();
    }

    // ── C. JS property setter (accepts array) ────────────────────────────
    set users(data) {
        this._users = Array.isArray(data) ? data : [];
        this._renderList();
        this._updateCount();
        this._bindEvents();
    }

    // ── D. Attribute → property bridge (accepts JSON attribute) ──────────
    static get observedAttributes() { return ["users"]; }
    attributeChangedCallback(name, _, newVal) {
        if (name === "users") {
            try { this.users = JSON.parse(newVal); } catch {}
        }
    }

    // ── E. Utility: format balance ────────────────────────────────────────
    _fmtBalance(v) {
        const sign = v < 0 ? "-" : v === 0 ? "" : "+";
        return sign + "CHF\u00A0" + Math.abs(v).toLocaleString("de-CH", {
            minimumFractionDigits: 2, maximumFractionDigits: 2
        });
    }

    // ── F. Utility: human-readable date ──────────────────────────────────
    _fmtDate(d) {
        //const date = new Date(d + "T00:00:00");
        if (d != undefined) {
            const date = new Date(d.replace(" ", "T"));

            const diff = Math.floor((new Date() - date) / 86400000);
            if (diff === 0) return t("today");
            if (diff === 1) return t("yesterday");
            if (diff < 7) return diff + t("day-ago");
            return date.toLocaleDateString("en-CH", { day: "numeric", month: "short" });
        } else {
            return t("unknown");
        }
    }

    // ── G. Filter + sort the user array ──────────────────────────────────
    _getFiltered() {
        return this._users
        .filter(u => {
            const text = `${u.name} ${u.surname} ${u.alias}`.toLowerCase();
            const matchQ = !this._query || text.includes(this._query);
            const matchP =
            this._filter === "all" ||
            (this._filter === "on" && u.push) ||
            (this._filter === "off" && !u.push);
            return matchQ && matchP;
        })
        //.sort((a, b) => b.lastLogin.localeCompare(a.lastLogin));
        .sort((a, b) => b.balance - a.balance);
    }

    // ── H. Build HTML for a single card ──────────────────────────────────
    _cardHTML(u) {
        const initials = (u.name[0] + u.surname[0]).toUpperCase();
        const avatarEl = u.avatar
        ? `<img class="avatar" src="${u.avatar}" alt="${u.name}"
        onerror="this.style.display='none';this.nextElementSibling.style.display='flex'">
        <div class="avatar-placeholder" style="display:none">${initials}</div>`
        : `<div class="avatar-placeholder">${initials}</div>`;

        const email = `<svg width="16" height="16" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
            <path fill="#5A4339" d="M128 224v512a64 64 0 0 0 64 64h640a64 64 0 0 0 64-64V224H128zm0-64h768a64 64 0 0 1 64 64v512a128 128 0 0 1-128 128H192A128 128 0 0 1 64 736V224a64 64 0 0 1 64-64z"/>
            <path fill="#5A4339" d="M904 224 656.512 506.88a192 192 0 0 1-289.024 0L120 224h784zm-698.944 0 210.56 240.704a128 128 0 0 0 192.704 0L818.944 224H205.056z"/>
        </svg>`;

        const push = `<svg width="16" height="16" viewBox="0 0 512 512" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <title>notification</title>
            <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                <g id="Chat" fill="#000000" transform="translate(64.000000, 85.333333)">
                    <path d="M384,1.42108547e-14 L384,298.666667 L277.333333,298.666667 L277.333333,384 L128,298.666667 L1.42108547e-14,298.666667 L1.42108547e-14,1.42108547e-14 L384,1.42108547e-14 Z M341.333333,42.6666667 L42.6666667,42.6666667 L42.6666667,256 L139.330749,256 L234.666667,310.464 L234.666667,256 L341.333333,256 L341.333333,42.6666667 Z">
                    </path>
                </g>
            </g>
        </svg>`;

        let notifyButton = "";
        let emailButton = "";
        if (u.push && u.balance < 999990) {
            notifyButton = `<div class="n-actions">
                <button class="n-btn n-btn-notify" data-id="${u.id}" title="${t("notify")}" aria-label="${t("inbox-notify")}">
                ${push}
                </button>
            </div>`;
        }
        if (u.balance < 999990) {
            emailButton = `<div class="n-actions">
                <a class="n-btn n-btn-notify" href="mailto:${u.id}?subject=CC-Caffè&body=Il tuo credito attuale è di ${u.balance} CHF. Il 'Dittatore' ti aspetta per saldare il tuo debito 💰💰💰!!">
                ${email}
                </a>
            </div>`;
        }
        

        return `
        <div class="user-card" data-id="${u.id}" role="listitem" tabindex="0" aria-label="${u.name} ${u.surname}, balance ${this._fmtBalance(u.balance)}">
            <div class="avatar-col">${avatarEl}</div>
            <div class="user-main">
                <p class="user-name">${u.name} ${u.surname}</p>
                <p class="user-alias">@${u.alias}</p>
                <div class="user-meta">
                    <span class="chip">🏷️ ${u.version}</span>
                    <span class="push-badge ${u.push ? 'on' : 'off'}">
                    <span class="dot"></span>${u.push ? 'Push on' : 'Push off'}</span>
                    <span class="chip">🔑 ${this._fmtDate(u.lastLogin)}</span>
                </div>
            </div>
            <div class="user-right">
                <span class="balance ${u.balance < 0 ? 'neg' : ''}">${this._fmtBalance(u.balance)}</span>
                ${notifyButton}
                ${emailButton}
            </div>
         </div>`;
            
    }

    // ── I. Re-render only the list section ────────────────────────────────
    _renderList() {
        const filtered = this._getFiltered();
        const listEl = this._shadow.getElementById("list");
        if (!listEl) return;

        listEl.innerHTML = filtered.length
        ? filtered.map(u => this._cardHTML(u)).join("")
        : `<p class="empty">No users found</p>`;
    }

    // ── J. Update the count label ─────────────────────────────────────────
    _updateCount() {
        const el = this._shadow.getElementById("count");
        if (!el) return;
        const n = this._getFiltered().length;
        el.textContent = n + " user" + (n !== 1 ? "s" : "");
    }

    // ── K. Bind search / filter / card-click events ───────────────────────
    _bindEvents() {
        const root = this._shadow;

        root.getElementById("search").addEventListener("input", e => {
            this._query = e.target.value.toLowerCase();
            this._renderList();
            this._updateCount();
        });

        root.getElementById("push-filter").addEventListener("change", e => {
            this._filter = e.target.value;
            this._renderList();
            this._updateCount();
        });

        root.getElementById("list").addEventListener("click", e => {
            const card = e.target.closest(".user-card");
            if (!card) return;
            //const user = this._users.find(u => u.id === Number(card.dataset.id));
            const user = this._users.find(u => u.id === card.dataset.id);
            if (user) this._dispatch(user);
        });

        root.getElementById("list").addEventListener("keydown", e => {
            if (e.key === "Enter" || e.key === " ") {
                const card = e.target.closest(".user-card");
                if (!card) return;
                //const user = this._users.find(u => u.id === Number(card.dataset.id));
            const user = this._users.find(u => u.id === card.dataset.id);
                if (user) this._dispatch(user);
            }
        });

        root.getElementById("list").querySelectorAll(".n-btn-notify").forEach(btn => {
            console.log("Adding notify event to button %o", btn);

            btn.addEventListener("click", e => {
                e.stopPropagation();
                const user = this._users.find(u => u.id === btn.dataset.id);
                this._notifyUser(user);
            });
        });
    }

    _notifyUser(user) {
        this.dispatchEvent(new CustomEvent("user-notify", {
            detail: user, bubbles: true, composed: true
        }));
    }

    // ── L. Fire a custom event so the host page can react ─────────────────
    _dispatch(user) {
        this.dispatchEvent(new CustomEvent("user-select", {
            detail: user, bubbles: true, composed: true
        }));
    }

    // ── M. Full initial render (shell + styles in Shadow DOM) ─────────────
    _render() {
        this._shadow.innerHTML = `
        <style>
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :host {
        display: block;
        font-family: system-ui, sans-serif;
        color: #1a1a1a;
        }

        .toolbar {
        display: flex;
        gap: 8px;
        margin-bottom: 10px;
        }

        input, select {
        font: inherit;
        font-size: 14px;
        padding: 8px 12px;
        border: 0.5px solid #d0cfc7;
        border-radius: 8px;
        background: #fff;
        outline: none;
        }

        input { flex: 1; }
        input:focus, select:focus { border-color: #888; }

        .header {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #888;
        margin-bottom: 8px;
        }

        .list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        }

        .user-card {
        background: #fff;
        border: 0.5px solid #e0dfd7;
        border-radius: 12px;
        padding: 14px 16px;
        display: grid;
        grid-template-columns: 48px 1fr auto;
        gap: 0 14px;
        align-items: start;
        cursor: pointer;
        transition: border-color 0.15s;
        }

        .user-card:hover { border-color: #bbb; }
        .user-card:focus-visible { outline: 2px solid #4a90d9; outline-offset: 2px; }

        .avatar-col { display: flex; flex-direction: column; align-items: center; }

        .avatar {
        width: 48px; height: 48px;
        border-radius: 50%;
        object-fit: cover;
        border: 0.5px solid #e0dfd7;
        }

        .avatar-placeholder {
        width: 48px; height: 48px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 500;
        background: #A28077;
        color: #F5EDEB;
        }

        .user-name {
        font-size: 15px;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 1px;
        }

        .user-alias {
        font-size: 12px;
        color: #888;
        margin-bottom: 8px;
        }

        .user-meta { display: flex; flex-wrap: wrap; gap: 5px; }

        .chip {
        font-size: 11px;
        padding: 2px 8px;
        border-radius: 99px;
        border: 0.5px solid #e0dfd7;
        color: #888;
        background: #f7f6f2;
        }

        .user-right {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 8px;
        padding-top: 2px;
        }

        .balance {
        font-size: 15px;
        font-weight: 500;
        letter-spacing: -0.02em;
        }

        .balance.neg { color: #c0392b; }

        .push-badge {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        padding: 2px 8px;
        border-radius: 99px;
        }

        .push-badge.on { background: #e6f4ea; color: #2d6a4f; }
        .push-badge.off { background: #f5f5f3; color: #aaa; border: 0.5px solid #e0dfd7; }

        #push-filter, #search {
          color: #000000;
        }

        .dot {
        width: 6px; height: 6px;
        border-radius: 50%;
        flex-shrink: 0;
        }

        .push-badge.on .dot { background: #2d6a4f; }
        .push-badge.off .dot { background: #ccc; }

        .empty {
        text-align: center;
        color: #aaa;
        padding: 2rem 0;
        font-size: 14px;
        }

        .n-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px; height: 28px;
            border-radius: 7px;
            border: 0.5px solid #e0dfd7;
            background: #fff;
            color: #888;
            cursor: pointer;
            transition: background 0.12s, color 0.12s, border-color 0.12s;
          }

          .n-btn-notify:hover  { background: #fdecea; color: #A28077; border-color: #f1c0bb; }

        @media (max-width: 400px) {
        .user-card { padding: 10px 12px; gap: 0 10px; }
        .balance { font-size: 13px; }
        }
        </style>

        <div class="toolbar">
        <input id="search" type="text" placeholder="Search by name or alias…" />
        <select id="push-filter">
        <option value="all">All users</option>
        <option value="on">Push on</option>
        <option value="off">Push off</option>
        </select>
        </div>

        <div class="header">
        <span id="count">0 users</span>
        <span>sorted by balance</span>
        </div>

        <div class="list" id="list" role="list"></div>
        `;
    }
}

export { UserList }

